import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DEFAULT_LANG,
  EN,
  LANGS,
  loadLocale,
  type Lang,
  type TKey,
} from "./translations";

type Ctx = {
  lang: Lang;
  t: (key: TKey) => string;
  /** Path prefix for the current language ("" for en, "/es" or "/pt" otherwise) */
  prefix: string;
  /** Build a localized URL for a given internal path (e.g. "/", "/privacy"). */
  localePath: (path: string) => string;
  /** Switch language, preserving current sub-path. */
  switchTo: (next: Lang) => void;
};

const LanguageContext = createContext<Ctx | null>(null);

export const isLang = (v: unknown): v is Lang =>
  typeof v === "string" && (LANGS as readonly string[]).includes(v);

const stripLangPrefix = (pathname: string) => {
  const seg = pathname.split("/").filter(Boolean);
  if (seg.length && (seg[0] === "es" || seg[0] === "pt")) {
    return "/" + seg.slice(1).join("/");
  }
  return pathname || "/";
};

// Module-level cache so each non-default locale chunk is only fetched once
// per session, regardless of how many times the LanguageProvider remounts.
const dictCache: Partial<Record<Lang, Readonly<Record<TKey, string>>>> = {
  en: EN,
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const firstSeg = location.pathname.split("/").filter(Boolean)[0];
  const lang: Lang = isLang(firstSeg) ? firstSeg : DEFAULT_LANG;
  const prefix = lang === DEFAULT_LANG ? "" : `/${lang}`;

  // Active dictionary. EN is always available synchronously; ES/PT fall back
  // to EN until their chunk loads (avoids blank UI / hydration mismatch).
  const [dict, setDict] = useState<Readonly<Record<TKey, string>>>(
    () => dictCache[lang] ?? EN,
  );

  useEffect(() => {
    const cached = dictCache[lang];
    if (cached) {
      setDict(cached);
      return;
    }
    let cancelled = false;
    void loadLocale(lang).then((d) => {
      dictCache[lang] = d;
      if (!cancelled) setDict(d);
    });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  const t = useCallback(
    (key: TKey) => dict[key] ?? EN[key] ?? String(key),
    [dict],
  );

  const localePath = useCallback(
    (path: string) => {
      const clean = path.startsWith("/") ? path : `/${path}`;
      if (clean === "/") return prefix || "/";
      return `${prefix}${clean}`;
    },
    [prefix],
  );

  const switchTo = useCallback(
    (next: Lang) => {
      const base = stripLangPrefix(location.pathname);
      const nextPath =
        next === DEFAULT_LANG
          ? base
          : base === "/"
          ? `/${next}`
          : `/${next}${base}`;
      try {
        localStorage.setItem("pps:lang", next);
      } catch {
        /* ignore */
      }
      navigate(`${nextPath}${location.hash || ""}`);
    },
    [location.hash, location.pathname, navigate],
  );

  const value = useMemo<Ctx>(
    () => ({ lang, t, prefix, localePath, switchTo }),
    [lang, t, prefix, localePath, switchTo],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLang = (): Ctx => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
};
